import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Mail, MapPin, Send, Check, AlertTriangle } from "lucide-react";
import { CONTACT } from "@/constants/testIds";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMAIL = "contact@umsoftware.id";
const MAP_LINK = "https://maps.app.goo.gl/rpmUVVrB8pJCcbGV9";
// Jakarta embed fallback (works without API key)
const MAP_EMBED = "https://www.google.com/maps?q=UM%20Software%20Jakarta&output=embed";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", subject: "", message: "" });
  const [status, setStatus] = useState({ state: "idle", message: "" }); // idle | loading | success | error

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setStatus({ state: "error", message: "Please fill in your name, email, and message." });
      return;
    }
    setStatus({ state: "loading", message: "" });
    try {
      await axios.post(`${API}/contact`, form);
      setStatus({ state: "success", message: "Transmission received. We'll get back to you within 24 hours." });
      setForm({ name: "", email: "", company: "", subject: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setStatus({
        state: "error",
        message: typeof detail === "string" ? detail : "Uplink failed. Please try again or email us directly.",
      });
    }
  };

  return (
    <section
      id="contact"
      data-testid={CONTACT.section}
      className="relative py-24 md:py-32 border-t border-cyan-900/40 overflow-hidden"
    >
      <div className="absolute inset-0 hud-grid opacity-25 pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 md:px-8 relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Left: info + map */}
          <div className="lg:col-span-5 flex flex-col gap-8">
            <div>
              <div className="overline">/ 04 open_channel</div>
              <h2 className="mt-4 font-mono-display text-3xl sm:text-4xl md:text-5xl font-extrabold uppercase tracking-tighter text-slate-100">
                Start your <span className="text-neon-cyan">mission</span>
              </h2>
              <p className="mt-5 text-slate-400 font-body">
                Have a build in mind? A live product to level up? Drop us a signal — our operators respond within one business day.
              </p>
            </div>

            <div className="flex flex-col gap-4">
              <a
                data-testid={CONTACT.emailLink}
                href={`mailto:${EMAIL}`}
                className="group flex items-center gap-4 p-4 bg-[#0a0a0a] border border-white/5 hover:border-cyan-500/50 transition-colors duration-200"
              >
                <span className="w-11 h-11 flex items-center justify-center border border-cyan-500/40 text-cyan-400 group-hover:bg-cyan-500/10 transition-colors">
                  <Mail size={18} />
                </span>
                <div>
                  <div className="font-hud text-cyan-400/70">email</div>
                  <div className="font-mono-display text-slate-100 tracking-tight">{EMAIL}</div>
                </div>
              </a>

              <a
                data-testid={CONTACT.mapLink}
                href={MAP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-4 p-4 bg-[#0a0a0a] border border-white/5 hover:border-fuchsia-500/50 transition-colors duration-200"
              >
                <span className="w-11 h-11 flex items-center justify-center border border-fuchsia-500/40 text-fuchsia-400 group-hover:bg-fuchsia-500/10 transition-colors">
                  <MapPin size={18} />
                </span>
                <div>
                  <div className="font-hud text-fuchsia-400/70">HQ · Jakarta, ID</div>
                  <div className="font-mono-display text-slate-100 tracking-tight text-sm">Open location on Google Maps →</div>
                </div>
              </a>
            </div>

            <div className="relative overflow-hidden border border-cyan-900/50 hud-corners h-64">
              <span className="hud-c-tl" />
              <span className="hud-c-br" />
              <iframe
                title="UM Software HQ Map"
                src={MAP_EMBED}
                loading="lazy"
                className="w-full h-full map-dark"
                style={{ border: 0 }}
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-7">
            <motion.form
              data-testid={CONTACT.form}
              onSubmit={onSubmit}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="relative p-6 md:p-10 bg-[#0a0a0a] border border-cyan-900/40 hud-corners"
            >
              <span className="hud-c-tl" />
              <span className="hud-c-br" />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <label className="font-hud text-cyan-400/70" htmlFor="name">/ name</label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={form.name}
                    onChange={onChange}
                    placeholder="Ada Lovelace"
                    className="hud-input"
                    data-testid={CONTACT.inputName}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="font-hud text-cyan-400/70" htmlFor="email">/ email</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={form.email}
                    onChange={onChange}
                    placeholder="you@studio.io"
                    className="hud-input"
                    data-testid={CONTACT.inputEmail}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="font-hud text-cyan-400/70" htmlFor="company">/ company</label>
                  <input
                    id="company"
                    name="company"
                    type="text"
                    value={form.company}
                    onChange={onChange}
                    placeholder="Optional"
                    className="hud-input"
                    data-testid={CONTACT.inputCompany}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="font-hud text-cyan-400/70" htmlFor="subject">/ subject</label>
                  <input
                    id="subject"
                    name="subject"
                    type="text"
                    value={form.subject}
                    onChange={onChange}
                    placeholder="e.g. Mobile app build"
                    className="hud-input"
                    data-testid={CONTACT.inputSubject}
                  />
                </div>
                <div className="flex flex-col gap-2 sm:col-span-2">
                  <label className="font-hud text-cyan-400/70" htmlFor="message">/ message</label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={6}
                    value={form.message}
                    onChange={onChange}
                    placeholder="Tell us about your mission..."
                    className="hud-input resize-none"
                    data-testid={CONTACT.inputMessage}
                  />
                </div>
              </div>

              <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <button
                  type="submit"
                  disabled={status.state === "loading"}
                  data-testid={CONTACT.submitBtn}
                  className="btn-neon disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {status.state === "loading" ? "Transmitting..." : (
                    <>Transmit_signal <Send size={13} /></>
                  )}
                </button>
                <span className="font-hud text-slate-500">
                  encrypted · &lt; 24h response · no spam
                </span>
              </div>

              {status.state === "success" && (
                <div
                  data-testid={CONTACT.successMsg}
                  className="mt-6 flex items-start gap-3 p-4 border border-cyan-500/40 bg-cyan-500/5 text-cyan-200 font-body"
                >
                  <Check size={16} className="mt-0.5 text-cyan-400" />
                  <span>{status.message}</span>
                </div>
              )}
              {status.state === "error" && (
                <div
                  data-testid={CONTACT.errorMsg}
                  className="mt-6 flex items-start gap-3 p-4 border border-red-500/40 bg-red-500/5 text-red-200 font-body"
                >
                  <AlertTriangle size={16} className="mt-0.5 text-red-400" />
                  <span>{status.message}</span>
                </div>
              )}
            </motion.form>
          </div>
        </div>
      </div>
    </section>
  );
}
